import axios from 'axios'
import {
  COPPEL_ABACUS_BASE,
  COPPEL_API_BASE,
  COPPEL_ENDPOINTS,
  COPPEL_USE_ABACUS_DIRECT,
} from './constants'
import { parseCoppelTemplatePayload } from './template'

/** Cliente dedicado a Abacus (no usar el axios de pcotizador/apipro). */
const abacusHttp = axios.create({
  timeout: 120000,
})

function parseApiPayload(response) {
  const data = response && response.data
  if (!data) return null

  if (data.objRespuesta !== undefined) {
    const raw = data.objRespuesta
    if (!raw) return null
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw)
      } catch (e) {
        return null
      }
    }
    return raw
  }

  if (data.data) return data
  return data
}

function apiBase() {
  return COPPEL_USE_ABACUS_DIRECT ? COPPEL_ABACUS_BASE : COPPEL_API_BASE
}

function jsonHeaders() {
  return { 'Content-Type': 'application/json; charset=UTF-8', Accept: 'application/json' }
}

let cachedTemplate = null
let templateFetchPromise = null

/** GET plantilla SOC; cache en memoria (`conditional_options` + `inputs`). */
export async function fetchCoppelSocTemplate() {
  if (cachedTemplate) return cachedTemplate
  if (templateFetchPromise) return templateFetchPromise

  if (!COPPEL_USE_ABACUS_DIRECT) {
    cachedTemplate = null
    return null
  }

  templateFetchPromise = abacusHttp
    .get(apiBase() + COPPEL_ENDPOINTS.template, { headers: jsonHeaders() })
    .then((response) => {
      const parsed = parseApiPayload(response)
      cachedTemplate = parseCoppelTemplatePayload(parsed)
      return cachedTemplate
    })
    .catch((err) => {
      console.warn('Coppel template', err)
      cachedTemplate = null
      return null
    })
    .finally(() => {
      templateFetchPromise = null
    })

  return templateFetchPromise
}

/** Limpia cache de plantilla (útil si Abacus actualiza reglas en caliente). */
export function resetCoppelSocTemplateCache() {
  cachedTemplate = null
  templateFetchPromise = null
}

/** Body según OpenAPI SOC: product_application.answers */
export function buildSocRequestBody(answers) {
  return { product_application: { answers } }
}

export async function postCoppelProductApplication(axios, answers) {
  const body = COPPEL_USE_ABACUS_DIRECT
    ? buildSocRequestBody(answers)
    : {
        answers,
        async: false,
        customer_id: '',
        external_id: '',
        dry_run: true,
      }

  const url = COPPEL_USE_ABACUS_DIRECT
    ? apiBase() + COPPEL_ENDPOINTS.productApplication
    : apiBase() + 'MethodoCoppel.php'

  const http = COPPEL_USE_ABACUS_DIRECT ? abacusHttp : axios
  const response = await http.post(url, body, {
    headers: jsonHeaders(),
    validateStatus: () => true,
  })
  const parsed = parseApiPayload(response)
  if (response.status >= 400) {
    return parsed && parsed.errors
      ? parsed
      : { errors: [`Abacus HTTP ${response.status}`], data: null }
  }
  return parsed
}

export async function fetchCoppelAmortization(axios, amortizationId) {
  if (!amortizationId) return null

  if (COPPEL_USE_ABACUS_DIRECT) {
    const response = await abacusHttp.get(apiBase() + COPPEL_ENDPOINTS.amortizationPrefix + amortizationId, {
      headers: jsonHeaders(),
    })
    return parseApiPayload(response)
  }

  const response = await axios.post(
    apiBase() + 'MethodoCoppelAmortizacion.php',
    { id: amortizationId },
    { headers: jsonHeaders() }
  )
  return parseApiPayload(response)
}

export async function fetchCoppelPdfUrl(axios, applicationId) {
  if (!applicationId) return null

  if (COPPEL_USE_ABACUS_DIRECT) {
    const url = apiBase() + COPPEL_ENDPOINTS.pdfPrefix + applicationId + '/pdf'
    return url
  }

  const response = await axios.post(
    apiBase() + 'MethodoCoppelPdf.php',
    { id: applicationId },
    { headers: jsonHeaders() }
  )
  const parsed = parseApiPayload(response)
  if (typeof parsed === 'string') return parsed
  if (parsed && parsed.url) return parsed.url
  if (response.data && response.data.url) return response.data.url
  return null
}

function esBufferPdf(buffer) {
  if (!buffer || !buffer.byteLength || buffer.byteLength < 5) return false
  const bytes = new Uint8Array(buffer.slice(0, 5))
  return (
    bytes[0] === 0x25 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x44 &&
    bytes[3] === 0x46
  )
}

/** Descarga el PDF desde Abacus en el navegador (nunca proxy pcotizador/AmortizacionIon.php). */
export async function fetchCoppelPdfBlob(_axios, applicationId) {
  if (!applicationId) return null

  const url =
    COPPEL_USE_ABACUS_DIRECT
      ? apiBase() + COPPEL_ENDPOINTS.pdfPrefix + applicationId + '/pdf'
      : null

  if (!url) return null

  try {
    const response = await abacusHttp.get(url, {
      responseType: 'arraybuffer',
      headers: { Accept: 'application/pdf' },
    })
    const data = response.data
    const contentType = String((response.headers && response.headers['content-type']) || '')
    if (!esBufferPdf(data) && !contentType.includes('pdf')) {
      console.warn('Coppel PDF: respuesta no es PDF válido', contentType)
      return null
    }
    return new Blob([data], { type: 'application/pdf' })
  } catch (e) {
    console.warn('Coppel PDF fetch', e)
    return null
  }
}

export function getAmortizationTableId(productPayload) {
  try {
    const attrs = productPayload.data.attributes || {}
    if (attrs.last_amortization_table) {
      return String(attrs.last_amortization_table)
    }
    const rel = productPayload.data.relationships.amortization_tables.data
    if (Array.isArray(rel) && rel.length && rel[0].id) {
      return String(rel[0].id)
    }
    if (rel && rel.id) {
      return String(rel.id)
    }
  } catch (e) {
    return null
  }
  return null
}
